/* AddOnPicker — recurring add-on multi-select shown under SelectedPlanBadge
 * on the register screen. Lets the customer bundle Payroll, Tax Return prep,
 * and Storage boosts into the same Stripe Checkout session as the plan.
 *
 * State flow:
 *   • The list of selected ids is owned by LoginDark.jsx (lifted up so
 *     it can be flushed into the `plan_intent` localStorage payload that
 *     drives the auto-checkout call in BillingSettings.handleCheckout).
 *   • This component is "dumb" — it only renders the toggles + a running
 *     monthly total so the customer sees the impact before they submit.
 */
import { Check, Plus, ChevronDown } from 'lucide-react';
import { useState } from 'react';

// Canonical list. Keep in lock-step with the ADDONS dict in
// backend/routes/billing.py — only recurring add-ons are eligible at signup.
export const SIGNUP_ADDONS = [
  {
    id: 'addon_payroll',
    label: 'Payroll',
    price: 199,
    blurb: 'Unlimited employees · tax filings · direct deposit',
    accent: 'emerald',
  },
  {
    id: 'addon_tax_return',
    label: 'Tax Return Prep & Filing',
    price: 49,
    blurb: 'CountBucks CPA files your annual federal + state return',
    accent: 'violet',
    // Platinum already includes tax-return prep, so we hide this row when
    // they're on Platinum.
    hideForPlan: (planId) => planId === 'platinum',
  },
  {
    id: 'addon_storage_50gb',
    label: '+50 GB storage',
    price: 4,
    blurb: 'For solo brands collecting receipts',
    accent: 'slate',
  },
  {
    id: 'addon_storage_250gb',
    label: '+250 GB storage',
    price: 10,
    blurb: 'For growing businesses with PDFs & contracts',
    accent: 'slate',
  },
  {
    id: 'addon_storage_1tb',
    label: '+1 TB storage',
    price: 24,
    blurb: 'For agencies and full archives',
    accent: 'slate',
  },
];

export default function AddOnPicker({ planId, cadence, selected = [], onChange, collapsible = false }) {
  const selectedSet = new Set(selected);
  const isAnnual = cadence === 'annual';
  const [open, setOpen] = useState(!collapsible);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  const items = SIGNUP_ADDONS.filter((a) => !a.hideForPlan?.(planId));
  const monthlyTotal = items
    .filter((a) => selectedSet.has(a.id))
    .reduce((s, a) => s + a.price, 0);

  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[#E4E8E6] bg-[#F7F9F8]/70 dark:border-white/10 dark:bg-white/[0.02] p-4 mt-4" data-testid="addon-picker">
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        className={`flex w-full items-center justify-between ${open ? 'mb-3' : ''} ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid="addon-picker-toggle"
      >
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#596273] dark:text-zinc-500">
          {collapsible ? 'Add optional extras' : 'Add-ons (optional · cancel anytime)'}
        </p>
        <span className="flex items-center gap-2">
          {monthlyTotal > 0 && (
            <span className="text-xs text-[#118A35] dark:text-emerald-300 font-mono" data-testid="addon-total">
              +${monthlyTotal.toFixed(monthlyTotal % 1 ? 2 : 0)}/mo
            </span>
          )}
          {collapsible && (
            <ChevronDown className={`w-4 h-4 text-[#9AA2AE] transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </span>
      </button>
      {open && (
      <div className="space-y-2">
        {items.map((a) => {
          const checked = selectedSet.has(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              data-testid={`addon-toggle-${a.id}`}
              aria-pressed={checked}
              className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
                checked
                  ? 'border-emerald-500/40 bg-emerald-500/[0.08]'
                  : 'border-white/10 bg-transparent hover:border-white/20 hover:bg-white/[0.03]'
              }`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <span
                  className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${
                    checked
                      ? 'bg-[#27B84A] border-[#27B84A] text-white'
                      : 'border-[#C4CBC7] dark:border-white/30 text-transparent'
                  }`}
                  aria-hidden
                >
                  {checked ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#18202D] dark:text-white">{a.label}</p>
                  <p className="text-[11px] text-[#596273] dark:text-zinc-500 truncate">{a.blurb}</p>
                </div>
              </div>
              <span className="text-sm font-mono whitespace-nowrap text-[#596273] dark:text-zinc-300">
                ${a.price}<span className="text-[#9AA2AE] dark:text-zinc-500">/mo</span>
              </span>
            </button>
          );
        })}
      </div>
      )}
      {open && isAnnual && monthlyTotal > 0 && (
        <p className="text-[11px] text-[#9AA2AE] dark:text-zinc-500 mt-3">
          Add-ons bill monthly even on annual plans — they aren&apos;t discounted.
        </p>
      )}
    </div>
  );
}

